import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ProfileSection = ({ profileData, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(profileData);
  const [uploadingLicense, setUploadingLicense] = useState(false);

  const specialtyOptions = [
    { value: 'radiology', label: 'Radiology' },
    { value: 'emergency', label: 'Emergency Medicine' },
    { value: 'general', label: 'General Practice' },
    { value: 'cardiology', label: 'Cardiology' },
    { value: 'orthopedics', label: 'Orthopedics' },
    { value: 'neurology', label: 'Neurology' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onUpdate(formData);
    setIsEditing(false);
  };

  const handleLicenseUpload = () => {
    setUploadingLicense(true);
    setTimeout(() => {
      setUploadingLicense(false);
      alert('License document uploaded successfully. Verification pending.');
    }, 2000);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Profile Information</h2>
        {!isEditing ? (
          <Button
            variant="outline"
            iconName="Edit"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
          >
            Edit Profile
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setFormData(profileData);
                setIsEditing(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              iconName="Save"
              iconPosition="left"
              onClick={handleSave}
            >
              Save Changes
            </Button>
          </div>
        )}
      </div>
      <div className="clinical-card p-6">
        <div className="flex items-start gap-6 mb-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-muted">
              <Image
                src={formData?.avatar}
                alt={formData?.avatarAlt}
                className="w-full h-full object-cover"
              />
            </div>
            {isEditing && (
              <button className="absolute bottom-0 right-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center shadow-md hover:bg-primary/90 transition-clinical">
                <Icon name="Camera" size={16} color="white" />
              </button>
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground">{formData?.name}</h3>
            <p className="text-sm text-muted-foreground">{formData?.email}</p>
            <div className="flex items-center gap-2 mt-2">
              <Icon name="Shield" size={16} color="var(--color-success)" />
              <span className="text-sm font-medium text-success">Verified Medical Professional</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Full Name"
            type="text"
            value={formData?.name}
            onChange={(e) => handleInputChange('name', e?.target?.value)}
            disabled={!isEditing}
            required
          />

          <Input
            label="Email Address"
            type="email"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            disabled={!isEditing}
            required
          />

          <Input
            label="Phone Number"
            type="tel"
            value={formData?.phone}
            onChange={(e) => handleInputChange('phone', e?.target?.value)}
            disabled={!isEditing}
          />

          <Select
            label="Medical Specialty"
            options={specialtyOptions}
            value={formData?.specialty}
            onChange={(value) => handleInputChange('specialty', value)}
            disabled={!isEditing}
            required
          />

          <Input
            label="Medical License Number"
            type="text"
            value={formData?.licenseNumber}
            onChange={(e) => handleInputChange('licenseNumber', e?.target?.value)}
            disabled={!isEditing}
            required
          />

          <Input
            label="License Expiry Date"
            type="date"
            value={formData?.licenseExpiry}
            onChange={(e) => handleInputChange('licenseExpiry', e?.target?.value)}
            disabled={!isEditing}
          />

          <Input
            label="Institution Name"
            type="text"
            value={formData?.institution}
            onChange={(e) => handleInputChange('institution', e?.target?.value)}
            disabled={!isEditing}
            className="md:col-span-2"
          />
        </div>

        <div className="mt-6 p-4 bg-muted rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="FileText" size={20} color="var(--color-primary)" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-foreground mb-1">Medical License Document</h4>
              <p className="text-xs text-muted-foreground mb-3">
                Upload a clear copy of your medical license for verification
              </p>
              <Button
                variant="outline"
                size="sm"
                iconName="Upload"
                iconPosition="left"
                loading={uploadingLicense}
                onClick={handleLicenseUpload}
              >
                {uploadingLicense ? 'Uploading...' : 'Re-upload License'}
              </Button>
            </div>
            <div className="status-badge status-badge-success">
              <Icon name="CheckCircle2" size={14} />
              <span className="ml-1">Verified</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSection;